import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  createDistributorAffiliateRegistration,
  fetchDistributors,
} from '@/lib/distributors'
import { ApiClientError } from '@/lib/api/client'
import type { Affiliate } from '@/types/affiliates'
import type { Distributor } from '@/types/distributors'
import styles from './AfiliadosListaPage.module.css'

function parseCommissionPercent(value: string): number | null {
  const num = parseFloat(value.replace(',', '.'))
  if (Number.isNaN(num) || num < 0.1 || num > 80) {
    return null
  }
  return num
}

interface AffiliateLinkDistributorModalProps {
  affiliate: Affiliate
  onClose: () => void
  onLinked: () => void
}

export function AffiliateLinkDistributorModal({
  affiliate,
  onClose,
  onLinked,
}: AffiliateLinkDistributorModalProps) {
  const [distributors, setDistributors] = useState<Distributor[]>([])
  const [distributorId, setDistributorId] = useState<number | ''>('')
  const [commissionPercent, setCommissionPercent] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedDistributor = distributors.find((d) => d.id === distributorId)
  const maxCap = selectedDistributor?.max_affiliate_commission_percent ?? 80

  useEffect(() => {
    void (async () => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await fetchDistributors()
        setDistributors(res.distributors)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar distribuidores.')
      } finally {
        setIsLoading(false)
      }
    })()
  }, [])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (distributorId === '') {
      setError('Selecione um distribuidor.')
      return
    }
    const pct = parseCommissionPercent(commissionPercent)
    if (pct === null) {
      setError(`Informe uma comissão entre 0,1% e ${maxCap}%.`)
      return
    }
    if (pct > maxCap) {
      setError(`A comissão não pode exceder o teto de ${maxCap}% deste distribuidor.`)
      return
    }

    const alreadyLinked = (affiliate.distributor_registrations ?? []).some(
      (r) => r.distributor_id === distributorId
    )
    if (alreadyLinked) {
      setError('Este afiliado já está vinculado a este distribuidor.')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await createDistributorAffiliateRegistration(distributorId, {
        platform_user_id: affiliate.platform_user_id,
        commission_percent: pct,
        all_campaigns: true,
        is_active: true,
      })
      onLinked()
      onClose()
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Erro ao vincular afiliado ao distribuidor.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.modalOverlay} role="presentation" onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-labelledby="link-distributor-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 id="link-distributor-title" className={styles.modalTitle}>
            Vincular a distribuidor
          </h2>
          <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Fechar">
            <Icon icon="mdi:close" />
          </button>
        </div>

        {isLoading ? (
          <p className={styles.modalBody}>Carregando distribuidores...</p>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div className={styles.modalBody}>
              <p className={styles.fieldHint}>
                Afiliado: <strong>{affiliate.affiliate_name}</strong> — será criado um novo link de
                indicação sob o distribuidor selecionado.
              </p>

            {error && (
              <div className={styles.formError}>
                <Icon icon="mdi:alert-circle-outline" />
                {error}
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="link-distributor-select">
                Distribuidor *
              </label>
              <select
                id="link-distributor-select"
                className={styles.input}
                value={distributorId === '' ? '' : String(distributorId)}
                onChange={(e) => {
                  const value = e.target.value
                  setDistributorId(value === '' ? '' : Number.parseInt(value, 10))
                }}
                required
              >
                <option value="">Selecione...</option>
                {distributors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.distributor_name} (teto afiliado: {d.max_affiliate_commission_percent}%)
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="link-distributor-commission">
                Comissão do afiliado neste canal (%) *
              </label>
              <input
                id="link-distributor-commission"
                className={styles.input}
                type="number"
                step="0.1"
                min="0.1"
                max={maxCap}
                value={commissionPercent}
                onChange={(e) => setCommissionPercent(e.target.value)}
                required
              />
            </div>
            </div>

            <div className={styles.modalFooter}>
              <button type="button" className={styles.btnCancel} onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </button>
              <button type="submit" className={styles.btnSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Vinculando...' : 'Vincular'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
